import { useCallback } from 'react';
import { Editor, Node, Point, Text as SlateText, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';

import {
  findAncestorByType,
  focusEditor,
  getCharacterAfterCursor,
  getCharacterBeforeCursor,
  insertBankMessage,
  insertExternalChannelItems,
  insertFileTextToPrompt,
  insertInstruction,
  insertResourceUrl,
  insertVariable,
  PROMPT_NODE_ID,
  removeNodeFromPromptById,
  removePrefixBeforeCaret,
  unwrapCurrentNode,
} from 'src/components/RichSlate';
import { MAX_RESOURCE_PER_TYPE } from 'src/data/common';
import { RESOURCE_CONTENT_TYPE } from 'src/data/performance';
import { captureMessage } from 'src/lib/sentry';
import { editorHasValidSelection, SLATE_TEXT_FORMATS } from 'src/lib/slate';
import { ROUTES } from 'src/routes';

import { PROMPT_MENU_TYPE } from '../RichSlate';

import { ACTION } from './const';

export const useHandlers = ({
  editor,
  handleMenuButtonClick,
  handleMenuPropsChange,
  handleCloseMenu,
  menuRef,
  shouldOpenConfirmPopup,
  onAction,
}) => {
  const handleOpenMenuAtCursor = useCallback(
    (target, menuProps) => {
      handleMenuButtonClick({ currentTarget: target });
      handleMenuPropsChange(menuProps);
    },
    [handleMenuButtonClick, handleMenuPropsChange]
  );

  const handleKeyDown = useCallback(
    (event) => {
      const { key } = event;
      const { selection } = editor;
      if (!selection) {
        return;
      }

      try {
        const [node, thisNodePath] = Editor.node(editor, selection);

        const { character: characterBeforeCursor } = getCharacterBeforeCursor(editor);
        const { character: characterAfterCursor } = getCharacterAfterCursor(editor);

        let { node: tagNode, path: tagNodePath } = findAncestorByType(
          editor,
          SLATE_TEXT_FORMATS.PROMPT_TAG
        );

        let noLengthCheck = false;

        if (!tagNode && !tagNodePath) {
          // node not found, find it in the children. happens in rare cases
          let thisNode;
          try {
            const theseChildren = Node.children(editor, [thisNodePath[0], thisNodePath[1] - 1]);
            thisNode = theseChildren.next();
            tagNode = thisNode.value[0];
            tagNodePath = thisNode.value[1].slice(0, 2);
            noLengthCheck = true;
          } catch {
            // nothing to do in this case
          }
        }

        if (tagNode) {
          const openingBracketRemovedWithBackspace =
            characterBeforeCursor === '[' && key === 'Backspace' && selection.anchor.offset === 1;
          const openingBracketRemovedWithDelete =
            characterAfterCursor === '[' && key === 'Delete' && selection.anchor.offset === 0;
          const openingBracketRemovedFromStart =
            openingBracketRemovedWithBackspace || openingBracketRemovedWithDelete;

          const closingBracketRemovedWithBackspace =
            characterBeforeCursor === ']' &&
            key === 'Backspace' &&
            (noLengthCheck || selection.anchor.offset === node.text.length);
          const closingBracketRemovedWithDelete =
            characterAfterCursor === ']' &&
            key === 'Delete' &&
            (noLengthCheck || selection.anchor.offset === node.text.length - 1);
          const closingBracketRemovedFromEnd =
            closingBracketRemovedWithBackspace || closingBracketRemovedWithDelete;

          if (openingBracketRemovedFromStart || closingBracketRemovedFromEnd) {
            unwrapCurrentNode(editor, tagNode, tagNodePath);
          }
          if (key === 'Enter' && !noLengthCheck) {
            // enter while on prompt tag is illegal
            event.preventDefault();
            return;
          }
        }

        const specialKeys = [
          'Backspace',
          'Tab',
          'Shift',
          'Control',
          'Alt',
          'CapsLock',
          'Escape',
          'PageUp',
          'PageDown',
          'End',
          'Home',
          'ArrowLeft',
          'ArrowUp',
          'ArrowRight',
          'ArrowDown',
          'Insert',
          'Delete',
          'NumLock',
          'ScrollLock',
          'Pause',
          'ContextMenu',
          'Meta',
          'F1',
          'F2',
          'F3',
          'F4',
          'F5',
          'F6',
          'F7',
          'F8',
          'F9',
          'F10',
          'F11',
          'F12',
          'F13',
          'F14',
          'F15',
        ];
        const isMetaOrCtrlPressed = event.ctrlKey || event.metaKey;
        // Exclude control keys and other special keys
        if (isMetaOrCtrlPressed || specialKeys.includes(key)) {
          return;
        }

        if (selection) {
          const [node, path] = Editor.node(editor, selection); // Get the currently selected node and its path

          // Get the range of the current node
          const start = Editor.start(editor, path);
          const end = Editor.end(editor, path);

          //avoid case of empty state
          if (SlateText.isText(node) && !node.text) {
            return;
          }

          // Check if the entire node is selected
          if (Point.equals(selection.anchor, start) && Point.equals(selection.focus, end)) {
            // Save the position where we want to insert the new character
            const pointBeforeRemoval = Editor.before(editor, start);

            if (path.length > 0) {
              if (
                !Editor.isStart(editor, selection.anchor, path) ||
                !Editor.isEnd(editor, selection.focus, path) ||
                !(node.type === SLATE_TEXT_FORMATS.PARAGRAPH)
              ) {
                Transforms.delete(editor, { at: selection });
              }
            }

            // Restore the selection
            if (pointBeforeRemoval) {
              Transforms.select(editor, pointBeforeRemoval);
            }

            //new character will be added naturally because we dont do event.preventDefault
          }
        }
      } catch (error) {
        captureMessage('Prompt field issue', {
          level: 'warning',
          extra: {
            error: error.toString(),
            source: 'PromptInstructions/useHandlers -> handleKeyDown',
          },
        });
      }
    },
    [editor]
  );

  const handleKeyDownMenu = useCallback(
    (event) => {
      if (['Enter', 'ArrowDown', 'ArrowUp', 'ArrowRight'].includes(event.key)) {
        if (
          menuRef?.current &&
          menuRef.current.contains(document.activeElement) &&
          document.activeElement.tagName.toLowerCase() !== 'li'
        ) {
          menuRef.current.querySelector('ul > li:not([disabled])').focus();
        }
        return;
      }
      handleKeyDown(event);
    },
    [handleKeyDown, menuRef]
  );

  const handleAction = useCallback(
    (action, data) => {
      switch (action) {
        case ACTION.insertBrandBrief: {
          handleCloseMenu();

          // Insert "brief" line into the editor
          const isTextSelected = editorHasValidSelection(editor);
          removePrefixBeforeCaret(editor);
          insertBankMessage(editor, data.item, isTextSelected);
          break;
        }

        case ACTION.insertUrl: {
          if (shouldOpenConfirmPopup([RESOURCE_CONTENT_TYPE.url])) {
            onAction(ACTION.displayMessage, {
              message: `You can use a maximum of ${MAX_RESOURCE_PER_TYPE} ${RESOURCE_CONTENT_TYPE.url.toUpperCase()}s`,
            });
            return;
          }

          handleCloseMenu();

          const isTextSelected = data.noTags ? true : editorHasValidSelection(editor);
          if (!ReactEditor.isFocused(editor)) {
            focusEditor({ editor, atEnd: true });
          }
          removePrefixBeforeCaret(editor);
          insertResourceUrl(editor, data.url, isTextSelected);
          break;
        }

        case ACTION.insertDocument: {
          if (
            !data.skipConfirmation &&
            shouldOpenConfirmPopup([RESOURCE_CONTENT_TYPE.document], data.item.id)
          ) {
            onAction(ACTION.displayMessage, {
              message: `You can use a maximum of ${MAX_RESOURCE_PER_TYPE} ${RESOURCE_CONTENT_TYPE.document}s`,
            });
            return;
          }

          // there is only a randomId in case of upload and then we don't want to close menu since it was
          // already closed and now reopened unrelated to this document
          if (!data.randomId) {
            handleCloseMenu();
          }

          removePrefixBeforeCaret(editor);
          insertFileTextToPrompt(
            editor,
            { file: data.item, type: RESOURCE_CONTENT_TYPE.document },
            { focus: true }
          );
          break;
        }

        case ACTION.insertImage: {
          if (
            !data.skipConfirmation &&
            shouldOpenConfirmPopup([RESOURCE_CONTENT_TYPE.image], data.item.id)
          ) {
            onAction(ACTION.displayMessage, {
              message: `You can use a maximum of ${MAX_RESOURCE_PER_TYPE} ${RESOURCE_CONTENT_TYPE.image}s`,
            });
            return;
          }

          if (!data.randomId) {
            handleCloseMenu();
          }

          // Insert "image" line into the editor
          removePrefixBeforeCaret(editor);
          insertFileTextToPrompt(
            editor,
            { file: data.item, type: RESOURCE_CONTENT_TYPE.image },
            { focus: true }
          );
          break;
        }

        case ACTION.removePromptImage: {
          removeNodeFromPromptById(editor, PROMPT_NODE_ID.image, data.label);
          break;
        }

        case ACTION.removePromptDocument: {
          removeNodeFromPromptById(editor, PROMPT_NODE_ID.document, data.label);
          break;
        }

        case ACTION.openResourceInNewTab: {
          // TODO: this "window.open" logic is duplicated several times across the App, we should unify it
          if (data.item.type === PROMPT_MENU_TYPE.image) {
            window.open(
              `${ROUTES.FILE_VIEWER}/${data.item.type}?url=${data.item.file.originalUrl}`,
              '_blank'
            );
          } else {
            const url = new URL(data.item.file.originalUrl);
            // replace + with %20 in pathname - specific issue with some urls
            const encodedPath = url.pathname.replace(/\+/g, '%2B');
            window.open(url.origin + encodedPath, '_blank', 'noopener,noreferrer');
          }
          break;
        }

        case ACTION.insertExternalChannelItems: {
          insertExternalChannelItems(editor, data.items);
          handleCloseMenu();
          break;
        }

        case ACTION.insertVariable: {
          const node = {
            type: SLATE_TEXT_FORMATS.PROMPT_VARIABLE,
            children: [
              {
                title: data.variable.title,
                info: data.variable.info,
                text: data.variable.text,
                infoTooltip: data.variable.tooltip,
              },
            ],
          };

          removePrefixBeforeCaret(editor);
          insertVariable(editor, node);
          break;
        }

        case ACTION.insertInstruction: {
          const node = {
            type: SLATE_TEXT_FORMATS.PROMPT_INSTRUCTION,
            children: [{ instruction: data.instruction.text }],
          };

          removePrefixBeforeCaret(editor);
          insertInstruction(editor, node);
          break;
        }

        default:
          break;
      }

      // Next, pass action to parent component
      return onAction(action, data);
    },
    [editor, handleCloseMenu, onAction, shouldOpenConfirmPopup]
  );

  return {
    handleOpenMenuAtCursor,
    handleKeyDown,
    handleKeyDownMenu,
    handleAction,
  };
};
